import { useState, useMemo, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence, Reorder } from "framer-motion";
import {
  Package,
  Plus,
  Calendar,
  FileText,
  Download,
  Trash2,
  Edit3,
  Eye,
  Users,
  MapPin,
  Loader2,
  ChevronLeft,
  CalendarDays,
  Building2,
  GripVertical,
  Euro,
  Send,
  UserPlus,
  X,
  Printer,
  Search,
  Check,
  ChevronDown,
  Undo2,
  Filter,
  Image,
  Tag,
  CheckCircle2,
  XCircle,
  Globe,
  Mail,
  Phone,
  Briefcase,
} from "lucide-react";
import { it } from "date-fns/locale";
import { format } from "date-fns";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Badge } from "../components/ui/Badge";
import { Spinner } from "../components/ui/Spinner";
import { ConfirmDialog } from "../components/ui/ConfirmDialog";
import { Dialog } from "../components/ui/Dialog";
import { useRequireAuth } from "../hooks/useAuth";
import { usePackagesStore } from "../stores/packagesStore";
import {
  usePackages,
  useCreatePackage,
  useUpdatePackage,
  useDeletePackage,
  useAddPackageItem,
  useUpdatePackageItem,
  useDeletePackageItem,
  usePublishPackage,
  useUnpublishPackage,
  useReorderPackageItems,
  useSearchOperators,
  useInviteCollaborator,
} from "../hooks/usePackages";
import { useSearchUsers } from "../hooks/useUsers";
import { cn } from "../lib/utils";
import { PackageCalendar } from "../components/packages/PackageCalendar";
import { AddressAutocomplete } from "../components/packages/AddressAutocomplete";
import { ArrayFieldInput } from "../components/packages/ArrayFieldInput";
import { ImageGalleryInput } from "../components/packages/ImageGalleryInput";
import { LanguageSelect } from "../components/packages/LanguageSelect";
import { CategorySelect, CATEGORIES } from "../components/packages/CategorySelect";
import {
  useServices,
  useCreateService,
} from "../hooks/useServices";
import type {
  Package as PackageType,
  PackageItem,
  PackageItemType,
  Operator,
  User,
  Service,
} from "../types/database";
import type { LucideIcon } from "lucide-react";

const itemTypeConfig: Record<
  PackageItemType,
  { label: string; color: string; icon: LucideIcon }
> = {
  event: { label: "Evento", color: "#284899", icon: CalendarDays },
  hotel: { label: "Hotel", color: "#3b82f6", icon: Building2 },
  activity: { label: "Attività", color: "#10b981", icon: MapPin },
  attraction: { label: "Attrazione", color: "#8b5cf6", icon: Eye },
  transport: { label: "Trasporto", color: "#6366f1", icon: MapPin },
  other: { label: "Altro", color: "#64748b", icon: Package },
};

const roleLabels: Record<string, string> = {
  "Operator": "Operatore",
  "operator": "Operatore",
  "LR": "Rappresentante Legale",
  "admin": "Amministratore",
  "collaborator": "Collaboratore",
  "owner": "Proprietario",
  "viewer": "Visualizzatore",
};

const getRoleLabel = (role: string | null | undefined): string => {
  if (!role) return "";
  return roleLabels[role] || role;
};

type ExtendedFieldKey = "location" | "category" | "images" | "included" | "tags" | "languages" | "contacts";

const EXTENDED_FIELD_OPTIONS: { key: ExtendedFieldKey; label: string; icon: LucideIcon }[] = [
  { key: "location", label: "Località", icon: MapPin },
  { key: "category", label: "Categoria", icon: Tag },
  { key: "images", label: "Immagini", icon: Image },
  { key: "included", label: "Incluso / Non incluso", icon: CheckCircle2 },
  { key: "tags", label: "Etichette", icon: Tag },
  { key: "languages", label: "Lingue", icon: Globe },
  { key: "contacts", label: "Contatti", icon: Mail },
];

function getInitialVisibleFields(item: PackageItem | Partial<PackageItem>): Set<ExtendedFieldKey> {
  const fields = new Set<ExtendedFieldKey>();
  if (item.location || item.city || item.address) fields.add("location");
  if (item.category && CATEGORIES.some(c => c.value === item.category)) fields.add("category");
  if (item.image_urls && item.image_urls.length > 0) fields.add("images");
  if ((item.included && item.included.length > 0) || (item.not_included && item.not_included.length > 0)) fields.add("included");
  if (item.tags && item.tags.length > 0) fields.add("tags");
  if (item.languages && item.languages.length > 0) fields.add("languages");
  if (item.email_contact || item.phone_contact) fields.add("contacts");
  return fields;
}

function AddDetailDropdown({
  visibleFields,
  onAdd,
}: {
  visibleFields: Set<ExtendedFieldKey>;
  onAdd: (field: ExtendedFieldKey) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const availableOptions = EXTENDED_FIELD_OPTIONS.filter((opt) => !visibleFields.has(opt.key));
  if (availableOptions.length === 0) return null;

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 text-sm font-medium text-[#284899] hover:text-[#1e3570] transition-colors"
      >
        <Plus className="h-4 w-4" />
        Aggiungi dettaglio
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 4 }}
            transition={{ duration: 0.15 }}
            className="absolute z-50 bottom-full mb-1 left-0 w-56 rounded-lg border border-border bg-card shadow-lg overflow-hidden"
          >
            {availableOptions.map((opt) => {
              const Icon = opt.icon;
              return (
                <button
                  key={opt.key}
                  type="button"
                  className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left hover:bg-secondary/50 transition-colors"
                  onClick={() => {
                    onAdd(opt.key);
                    setIsOpen(false);
                  }}
                >
                  <Icon className="h-4 w-4 text-muted-foreground" />
                  {opt.label}
                </button>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export function Packages() {
  const { isLoading: authLoading } = useRequireAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  // Store state
  const {
    selectedPackage,
    setSelectedPackage,
    currentPackageItems,
    setIsCreating,
    resetCurrentPackage,
  } = usePackagesStore();

  // Local state
  const [activeView, setActiveView] = useState<"list" | "create" | "detail">(
    "list"
  );
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [statusFilter, setStatusFilter] = useState<
    "all" | "draft" | "active" | "pending_approval" | "archived"
  >("all");

  // Confirm dialog state
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    confirmText: string;
    variant: "danger" | "warning";
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: "",
    message: "",
    confirmText: "",
    variant: "danger",
    onConfirm: () => { },
  });

  // Form state for new package
  const [newPackageData, setNewPackageData] = useState({
    name: "",
    description: "",
    start_date: "",
    end_date: "",
  });

  // Queries
  const { data: packages = [], isLoading: packagesLoading } = usePackages();

  // Handle URL parameter to open package directly
  useEffect(() => {
    const packageId = searchParams.get("id");
    if (packageId && packages.length > 0 && !packagesLoading) {
      const pkg = packages.find((p) => p.id === packageId);
      if (pkg) {
        setSelectedPackage(pkg);
        setActiveView("detail");
        // Clear the URL parameter
        setSearchParams({}, { replace: true });
      }
    }
  }, [searchParams, packages, packagesLoading, setSelectedPackage, setSearchParams]);

  // Mutations
  const createPackage = useCreatePackage();
  const deletePackage = useDeletePackage();
  const publishPackage = usePublishPackage();
  const unpublishPackage = useUnpublishPackage();

  // Stats
  const stats = useMemo(
    () => ({
      total: packages.length,
      draft: packages.filter((p) => p.status === "draft").length,
      active: packages.filter((p) => p.status === "active").length,
      pending: packages.filter((p) => p.status === "pending_approval").length,
    }),
    [packages]
  );

  // Handlers
  const handleCreatePackage = async () => {
    if (!newPackageData.name.trim()) return;

    try {
      const pkg = await createPackage.mutateAsync({
        name: newPackageData.name,
        description: newPackageData.description || undefined,
        start_date: newPackageData.start_date || undefined,
        end_date: newPackageData.end_date || undefined,
      });
      setNewPackageData({
        name: "",
        description: "",
        start_date: "",
        end_date: "",
      });
      setSelectedPackage(pkg);
      setActiveView("detail");
      setIsCreating(false);
    } catch (error) {
      console.error("Error creating package:", error);
    }
  };

  const handleDeletePackage = (id: string) => {
    setConfirmDialog({
      isOpen: true,
      title: "Elimina Pianificazione",
      message:
        "Sei sicuro di eliminare questa pianificazione? Questa azione non può essere annullata.",
      confirmText: "Elimina",
      variant: "danger",
      onConfirm: async () => {
        try {
          await deletePackage.mutateAsync(id);
          if (selectedPackage?.id === id) {
            resetCurrentPackage();
            setActiveView("list");
          }
        } catch (error) {
          console.error("Error deleting package:", error);
        }
        setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
      },
    });
  };

  const handlePublish = async (id: string) => {
    try {
      await publishPackage.mutateAsync(id);
    } catch (error) {
      console.error("Error publishing package:", error);
    }
  };

  const handleUnpublish = (id: string) => {
    setConfirmDialog({
      isOpen: true,
      title: "Annulla Pubblicazione",
      message:
        "Sei sicuro di voler annullare la pubblicazione? La pianificazione tornerà in stato bozza.",
      confirmText: "Annulla Pubblicazione",
      variant: "warning",
      onConfirm: async () => {
        try {
          await unpublishPackage.mutateAsync(id);
        } catch (error) {
          console.error("Error unpublishing package:", error);
        }
        setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
      },
    });
  };

  const handleGeneratePdf = () => {
    if (!selectedPackage) return;
    setShowPdfPreview(true);
  };

  if (authLoading) {
    return (
      <div className="min-h-[calc(100vh-64px)] flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-64px)] p-6 lg:p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header & Stats - Hidden in detail view */}
        {activeView !== "detail" && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-6"
          >
            {/* Header */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex flex-col sm:flex-row sm:items-center justify-between gap-4"
            >
              <div>
                <h1 className="text-2xl font-bold flex items-center gap-2">
                  <Package className="h-6 w-6 text-[#284899]" />
                  Pianificazione Territoriale
                </h1>
                <p className="text-muted-foreground mt-1">
                  Organizza su calendario interattivo le esperienze territoriali da suggerire ai tuoi clienti
                </p>
              </div>

              <Button
                onClick={() => {
                  setIsCreating(true);
                  setActiveView("create");
                }}
              >
                <Plus className="h-4 w-4 mr-2" />
                Nuova pianificazione
              </Button>
            </motion.div>

            {/* Stats - Clickable to filter */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {[
                {
                  label: "Totali",
                  value: stats.total,
                  accent: "bg-slate-500",
                  textColor: "text-foreground",
                  filter: "all" as const,
                },
                {
                  label: "Bozze",
                  value: stats.draft,
                  accent: "bg-slate-400",
                  textColor: "text-muted-foreground",
                  filter: "draft" as const,
                },
                {
                  label: "Attivi",
                  value: stats.active,
                  accent: "bg-emerald-500",
                  textColor: "text-emerald-500",
                  filter: "active" as const,
                },
                {
                  label: "In Approvazione",
                  value: stats.pending,
                  accent: "bg-[#284899]",
                  textColor: "text-[#284899]",
                  filter: "pending_approval" as const,
                },
              ].map((stat, i) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                  className="group cursor-pointer"
                  onClick={() => setStatusFilter(stat.filter)}
                >
                  <Card
                    className={cn(
                      "relative overflow-hidden transition-all duration-200 hover:scale-[1.02]",
                      statusFilter === stat.filter &&
                      "border-[#284899] shadow-lg shadow-[#284899]/20"
                    )}
                  >
                    <div
                      className={cn(
                        "absolute top-0 left-0 right-0 h-1 transition-all duration-200 group-hover:h-1.5",
                        stat.accent
                      )}
                    />
                    <CardContent className="p-5 pt-4">
                      <div className="space-y-1">
                        <span
                          className={cn(
                            "text-3xl font-bold tracking-tight",
                            stat.textColor
                          )}
                        >
                          {stat.value}
                        </span>
                        <p className="text-sm text-muted-foreground">
                          {stat.label}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Main Content */}
        <AnimatePresence mode="wait">
          {activeView === "list" && (
            <motion.div
              key="list"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <PackagesList
                packages={packages}
                isLoading={packagesLoading}
                onSelect={(pkg) => {
                  setSelectedPackage(pkg);
                  setActiveView("detail");
                }}
                onDelete={handleDeletePackage}
                statusFilter={statusFilter}
                onStatusFilterChange={setStatusFilter}
              />
            </motion.div>
          )}

          {activeView === "create" && (
            <motion.div
              key="create"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
            >
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Plus className="h-5 w-5 text-[#284899]" />
                    Crea nuova pianificazione territoriale
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium">
                        Nome Pianificazione *
                      </label>
                      <Input
                        placeholder="Es: Weekend in Toscana"
                        value={newPackageData.name}
                        onChange={(e) =>
                          setNewPackageData((prev) => ({
                            ...prev,
                            name: e.target.value,
                          }))
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Descrizione</label>
                      <Input
                        placeholder="Breve descrizione della pianificazione"
                        value={newPackageData.description}
                        onChange={(e) =>
                          setNewPackageData((prev) => ({
                            ...prev,
                            description: e.target.value,
                          }))
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Data Inizio</label>
                      <Input
                        type="date"
                        value={newPackageData.start_date}
                        onChange={(e) =>
                          setNewPackageData((prev) => ({
                            ...prev,
                            start_date: e.target.value,
                          }))
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Data Fine</label>
                      <Input
                        type="date"
                        value={newPackageData.end_date}
                        onChange={(e) =>
                          setNewPackageData((prev) => ({
                            ...prev,
                            end_date: e.target.value,
                          }))
                        }
                      />
                    </div>
                  </div>

                  <div className="flex gap-3 pt-4">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setIsCreating(false);
                        setActiveView("list");
                      }}
                    >
                      Annulla
                    </Button>
                    <Button
                      onClick={handleCreatePackage}
                      disabled={
                        !newPackageData.name.trim() || createPackage.isPending
                      }
                    >
                      {createPackage.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <Plus className="h-4 w-4 mr-2" />
                      )}
                      Crea Pianificazione
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {activeView === "detail" && selectedPackage && (
            <motion.div
              key="detail"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <PackageDetail
                package={selectedPackage}
                onBack={() => {
                  resetCurrentPackage();
                  setActiveView("list");
                }}
                onGeneratePdf={handleGeneratePdf}
                onPublish={() => handlePublish(selectedPackage.id)}
                onUnpublish={() => handleUnpublish(selectedPackage.id)}
                onDelete={() => handleDeletePackage(selectedPackage.id)}
              />
            </motion.div>
          )}
        </AnimatePresence>

        {/* PDF Preview Modal */}
        <AnimatePresence>
          {showPdfPreview && selectedPackage && (
            <PdfPreviewModal
              package={selectedPackage}
              items={currentPackageItems}
              onClose={() => setShowPdfPreview(false)}
            />
          )}
        </AnimatePresence>

        {/* Confirm Dialog */}
        <ConfirmDialog
          isOpen={confirmDialog.isOpen}
          title={confirmDialog.title}
          message={confirmDialog.message}
          confirmText={confirmDialog.confirmText}
          variant={confirmDialog.variant}
          onConfirm={confirmDialog.onConfirm}
          onCancel={() =>
            setConfirmDialog((prev) => ({ ...prev, isOpen: false }))
          }
        />
      </div>
    </div>
  );
}

// ============================================
// SUB-COMPONENTS
// ============================================

function PackagesList({
  packages,
  isLoading,
  onSelect,
  onDelete,
  statusFilter,
  onStatusFilterChange,
}: {
  packages: PackageType[];
  isLoading: boolean;
  onSelect: (pkg: PackageType) => void;
  onDelete: (id: string) => void;
  statusFilter: "all" | "draft" | "active" | "pending_approval" | "archived";
  onStatusFilterChange: (
    filter: "all" | "draft" | "active" | "pending_approval" | "archived"
  ) => void;
}) {
  const [searchQuery, setSearchQuery] = useState("");

  // Filter packages
  const filteredPackages = useMemo(() => {
    let result = packages;

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (pkg) =>
          pkg.name.toLowerCase().includes(query) ||
          pkg.description?.toLowerCase().includes(query)
      );
    }

    // Filter by status
    if (statusFilter !== "all") {
      result = result.filter((pkg) => pkg.status === statusFilter);
    }

    return result;
  }, [packages, searchQuery, statusFilter]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  if (packages.length === 0) {
    return (
      <Card>
        <CardContent className="p-12 text-center">
          <Package className="h-16 w-16 text-muted-foreground opacity-30 mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">Nessuna pianificazione</h3>
          <p className="text-muted-foreground mb-4">
            Crea la tua prima pianificazione territoriale per iniziare
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex-1">
          <Input
            placeholder="Cerca pianificazione per nome..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            icon={<Search className="h-4 w-4" />}
          />
        </div>
        <div className="relative">
          <Filter className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <select
            value={statusFilter}
            onChange={(e) =>
              onStatusFilterChange(e.target.value as typeof statusFilter)
            }
            className="h-10 rounded-lg border border-border bg-card pl-9 pr-3 text-sm focus:border-[#284899] focus:outline-none appearance-none cursor-pointer"
          >
            <option value="all">Tutti gli stati</option>
            <option value="draft">Bozze</option>
            <option value="active">Attivi</option>
            <option value="pending_approval">In Approvazione</option>
            <option value="archived">Archiviati</option>
          </select>
          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
        </div>
      </div>

      {/* Results count */}
      <p className="text-sm text-muted-foreground">
        {filteredPackages.length} pianificazion
        {filteredPackages.length === 1 ? "e" : "i"}
        {(searchQuery || statusFilter !== "all") &&
          " trovat" + (filteredPackages.length === 1 ? "o" : "i")}
        {searchQuery && ` per "${searchQuery}"`}
      </p>

      {/* Package Grid */}
      {filteredPackages.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredPackages.map((pkg, i) => (
            <motion.div
              key={pkg.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
            >
              <PackageCard
                package={pkg}
                onSelect={() => onSelect(pkg)}
                onDelete={() => onDelete(pkg.id)}
              />
            </motion.div>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="p-12 text-center">
            <Search className="h-12 w-12 text-muted-foreground opacity-30 mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">Nessun risultato</h3>
            <p className="text-muted-foreground mb-4">
              Nessuna pianificazione corrisponde ai filtri selezionati
            </p>
            <Button
              variant="outline"
              onClick={() => {
                setSearchQuery("");
                onStatusFilterChange("all");
              }}
            >
              Resetta filtri
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function PackageCard({
  package: pkg,
  onSelect,
  onDelete,
}: {
  package: PackageType;
  onSelect: () => void;
  onDelete: () => void;
}) {
  const statusConfig = {
    draft: { label: "Bozza", color: "bg-muted text-muted-foreground" },
    pending_approval: {
      label: "In Approvazione",
      color: "bg-[#284899]/20 text-[#1e3570]",
    },
    active: { label: "Attivo", color: "bg-emerald-500/20 text-emerald-600" },
    archived: { label: "Archiviato", color: "bg-muted text-muted-foreground" },
  };

  const status = statusConfig[pkg.status];
  const itemCount = pkg.items?.length || 0;
  const collaboratorCount =
    pkg.collaborators?.filter((c) => c.role !== "owner").length || 0;

  return (
    <Card className="group relative overflow-hidden transition-all duration-200 hover:scale-[1.01] hover:border-[#284899]/50 h-full flex flex-col">
      <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-[#284899] to-[#1e3570] opacity-0 group-hover:opacity-100 transition-opacity" />
      <CardContent className="p-5 flex flex-col flex-1">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-lg group-hover:text-[#284899] transition-colors">
              {pkg.name}
            </h3>
            <Badge className={cn("mt-2 font-medium", status.color)}>
              {status.label}
            </Badge>
          </div>
          <div className="flex gap-1 opacity-60 group-hover:opacity-100 transition-opacity">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 hover:bg-[#284899]/10"
              onClick={onSelect}
            >
              <Eye className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-500/10"
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {pkg.description && (
          <p className="text-sm text-muted-foreground mt-3 line-clamp-2 leading-relaxed">
            {pkg.description}
          </p>
        )}

        <div className="flex-1" />

        <div className="flex items-center flex-wrap gap-4 mt-4 text-sm">
          {(pkg.start_date || pkg.end_date) && (
            <span className="flex items-center gap-1.5 text-muted-foreground bg-secondary/50 px-2.5 py-1 rounded-full">
              <Calendar className="h-3.5 w-3.5 text-[#284899]" />
              {pkg.start_date && format(new Date(pkg.start_date), "dd/MM")}
              {pkg.start_date && pkg.end_date && " - "}
              {pkg.end_date && format(new Date(pkg.end_date), "dd/MM/yy")}
            </span>
          )}
          <span className="flex items-center gap-1.5 text-muted-foreground bg-secondary/50 px-2.5 py-1 rounded-full">
            <Package className="h-3.5 w-3.5 text-blue-500" />
            {itemCount} servizi
          </span>
          {collaboratorCount > 0 && (
            <span className="flex items-center gap-1.5 text-muted-foreground bg-secondary/50 px-2.5 py-1 rounded-full">
              <Users className="h-3.5 w-3.5 text-emerald-500" />
              {collaboratorCount}
            </span>
          )}
        </div>

        <div className="flex gap-2 mt-4 pt-4 border-t border-border/50">
          <Button
            variant="outline"
            size="sm"
            className="flex-1 hover:border-[#284899]/50"
            onClick={onSelect}
          >
            <Edit3 className="h-3.5 w-3.5 mr-1.5" />
            Modifica
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function PackageDetail({
  package: pkg,
  onBack,
  onGeneratePdf,
  onPublish,
  onUnpublish,
  onDelete,
}: {
  package: PackageType;
  onBack: () => void;
  onGeneratePdf: () => void;
  onPublish: () => void;
  onUnpublish: () => void;
  onDelete: () => void;
}) {
  const [isAddingItem, setIsAddingItem] = useState(false);
  const [orderedItems, setOrderedItems] = useState<PackageItem[]>([]);

  // Edit package state
  const [showEditModal, setShowEditModal] = useState(false);

  // Edit item state
  const [editingItem, setEditingItem] = useState<PackageItem | null>(null);
  const [showEditItemModal, setShowEditItemModal] = useState(false);

  // Invite collaborator state
  const [showInviteModal, setShowInviteModal] = useState(false);

  // Calendar selected item state for bidirectional selection
  const [calendarSelectedItemId, setCalendarSelectedItemId] = useState<string | null>(null);

  const deletePackageItem = useDeletePackageItem();
  const updatePackageItem = useUpdatePackageItem();
  const reorderItems = useReorderPackageItems();
  const updatePackage = useUpdatePackage();
  const inviteCollaborator = useInviteCollaborator();
  const [showCollaboratorsDropdown, setShowCollaboratorsDropdown] = useState(false);

  // Confirm dialog state for item deletion
  const [itemConfirmDialog, setItemConfirmDialog] = useState<{
    isOpen: boolean;
    itemId: string;
  }>({
    isOpen: false,
    itemId: "",
  });

  const items = pkg.items || [];

  // Sync ordered items when package items change
  useEffect(() => {
    setOrderedItems(
      [...items].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
    );
  }, [items]);

  const handleDeleteItem = (itemId: string) => {
    setItemConfirmDialog({
      isOpen: true,
      itemId,
    });
  };

  const confirmDeleteItem = async () => {
    try {
      await deletePackageItem.mutateAsync({
        id: itemConfirmDialog.itemId,
        packageId: pkg.id,
      });
    } catch (error) {
      console.error("Error deleting item:", error);
    }
    setItemConfirmDialog({ isOpen: false, itemId: "" });
  };

  const statusConfig = {
    draft: { label: "Bozza", color: "bg-muted text-muted-foreground" },
    pending_approval: {
      label: "In Approvazione",
      color: "bg-[#284899]/20 text-[#1e3570]",
    },
    active: { label: "Attivo", color: "bg-emerald-500/20 text-emerald-600" },
    archived: { label: "Archiviato", color: "bg-muted text-muted-foreground" },
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={onBack}>
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <div>
            <h2 className="text-xl font-bold">{pkg.name}</h2>
            <Badge className={cn("mt-1", statusConfig[pkg.status].color)}>
              {statusConfig[pkg.status].label}
            </Badge>
          </div>
        </div>
        <div className="flex gap-2">
          {/* Collaborators Dropdown */}
          <div className="relative">
            <Button
              variant="outline"
              onClick={() => setShowCollaboratorsDropdown(!showCollaboratorsDropdown)}
            >
              <Users className="h-4 w-4 mr-2" />
              Collaboratori ({pkg.collaborators?.length || 0})
              <ChevronDown
                className={cn(
                  "h-4 w-4 ml-2 transition-transform",
                  showCollaboratorsDropdown && "rotate-180"
                )}
              />
            </Button>
            <AnimatePresence>
              {showCollaboratorsDropdown && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                  className="absolute right-0 top-full mt-2 w-80 bg-card border border-border rounded-lg shadow-lg z-50 overflow-hidden"
                >
                  <div className="p-3 border-b border-border bg-muted/30 flex items-center justify-between">
                    <h4 className="font-medium text-sm flex items-center gap-2">
                      <Users className="h-4 w-4 text-[#284899]" />
                      Collaboratori
                    </h4>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setShowInviteModal(true);
                        setShowCollaboratorsDropdown(false);
                      }}
                      disabled={pkg.status === "archived"}
                    >
                      <UserPlus className="h-4 w-4 mr-1" />
                      Invita
                    </Button>
                  </div>
                  <div className="p-3 max-h-72 overflow-y-auto">
                    {pkg.collaborators && pkg.collaborators.length > 0 ? (
                      <div className="space-y-2">
                        {pkg.collaborators.map((collab) => (
                          <div
                            key={collab.id}
                            className="flex items-center gap-3 p-2 rounded-lg bg-secondary/30"
                          >
                            <div className="w-8 h-8 rounded-full bg-[#284899]/20 flex items-center justify-center shrink-0">
                              <Users className="h-4 w-4 text-[#284899]" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">
                                {collab.user?.first_name} {collab.user?.last_name}
                              </p>
                              {collab.user?.email && (
                                <p className="text-sm text-muted-foreground truncate">
                                  {collab.user.email}
                                </p>
                              )}
                              <p className="text-sm text-muted-foreground">
                                {getRoleLabel(collab.role)}
                              </p>
                            </div>
                            <Badge
                              variant="secondary"
                              className={cn(
                                "shrink-0 text-sm",
                                collab.status === "accepted" &&
                                "bg-emerald-500/20 text-emerald-600",
                                collab.status === "pending" &&
                                "bg-[#284899]/20 text-[#1e3570]",
                                collab.status === "rejected" &&
                                "bg-red-500/20 text-red-600"
                              )}
                            >
                              {collab.status === "accepted"
                                ? "Accettato"
                                : collab.status === "pending"
                                  ? "In attesa"
                                  : "Rifiutato"}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-6 text-muted-foreground">
                        <Users className="h-8 w-8 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">Nessun collaboratore</p>
                        <p className="text-sm">
                          Invita altri operatori per pianificazioni congiunte
                        </p>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Action Buttons */}
          <Button
            variant="outline"
            onClick={onGeneratePdf}
          >
            <Download className="h-4 w-4 mr-2" />
            PDF
          </Button>

          {(pkg.status === "active" || pkg.status === "pending_approval") && (
            <Button
              variant="outline"
              onClick={onUnpublish}
            >
              <Undo2 className="h-4 w-4 mr-2" />
              Riporta in Bozza
            </Button>
          )}

          {pkg.status === "draft" && (
            <Button onClick={onPublish}>
              <Send className="h-4 w-4 mr-2" />
              Pubblica
            </Button>
          )}

          <Button
            variant="outline"
            className="text-red-500 hover:text-red-600 hover:bg-red-500/10"
            onClick={onDelete}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Left: Package Items List (2/5 width) */}
        <div className="lg:col-span-2 space-y-6">
          {/* Package Info */}
          <Card>
            <CardHeader className="flex-row items-center justify-between space-y-0 pb-4">
              <CardTitle className="text-base">Dettagli pianificazione</CardTitle>
              {pkg.status === "draft" && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowEditModal(true)}
                >
                  <Edit3 className="h-4 w-4 mr-1" />
                  Modifica
                </Button>
              )}
            </CardHeader>
            <CardContent className="space-y-3">
              {pkg.description ? (
                <p className="text-sm text-muted-foreground">
                  {pkg.description}
                </p>
              ) : (
                <p className="text-sm text-muted-foreground/50 italic">
                  Nessuna descrizione
                </p>
              )}
              <div className="flex flex-wrap gap-4 text-sm">
                {pkg.start_date || pkg.end_date ? (
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <span>
                      {pkg.start_date &&
                        format(new Date(pkg.start_date), "dd MMMM yyyy", {
                          locale: it,
                        })}
                      {pkg.start_date && pkg.end_date && " - "}
                      {pkg.end_date &&
                        format(new Date(pkg.end_date), "dd MMMM yyyy", {
                          locale: it,
                        })}
                    </span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-muted-foreground/50">
                    <Calendar className="h-4 w-4" />
                    <span className="italic">Date non impostate</span>
                  </div>
                )}
                {pkg.total_price && (
                  <div className="flex items-center gap-2">
                    <Euro className="h-4 w-4 text-muted-foreground" />
                    <span>
                      {pkg.total_price.toFixed(2)} {pkg.currency}
                    </span>
                  </div>
                )}
              </div>
              {/* Last modified info */}
              <div className="pt-2 border-t border-border mt-3">
                <p className="text-sm text-muted-foreground">
                  Ultima modifica:{" "}
                  {format(new Date(pkg.updated_at), "dd/MM/yyyy HH:mm", {
                    locale: it,
                  })}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Items */}
          <Card>
            <CardHeader className="flex-row items-center justify-between space-y-0 pb-4">
              <CardTitle className="text-base">
                Servizi territoriali da suggerire
              </CardTitle>
              <Button size="sm" onClick={() => setIsAddingItem(true)}>
                <Plus className="h-4 w-4 mr-1" />
                Aggiungi
              </Button>
            </CardHeader>
            <CardContent>
              {/* Items List with Drag & Drop */}
              {orderedItems.length > 0 ? (
                <Reorder.Group
                  axis="y"
                  values={orderedItems}
                  onReorder={(newOrder) => {
                    setOrderedItems(newOrder);
                    // Debounce the API call
                    reorderItems.mutate({ packageId: pkg.id, items: newOrder });
                  }}
                  className="space-y-2"
                >
                  {orderedItems.map((item) => {
                    const config = itemTypeConfig[item.item_type];
                    const Icon = config.icon;
                    const isSelected = item.id === calendarSelectedItemId;
                    return (
                      <Reorder.Item
                        key={item.id}
                        value={item}
                        className={cn(
                          "flex items-center gap-3 p-3 rounded-lg border group cursor-grab active:cursor-grabbing transition-all",
                          isSelected
                            ? "bg-[#284899]/10 border-[#284899] shadow-lg ring-2 ring-[#284899]/30"
                            : "bg-secondary/30 border-border hover:border-[#284899]/30"
                        )}
                        whileDrag={{
                          scale: 1.02,
                          boxShadow: "0 8px 20px rgba(0,0,0,0.12)",
                          backgroundColor: "hsl(var(--card))",
                        }}
                        onClick={() => {
                          // Bidirectional selection: click on item to highlight it in calendar
                          setCalendarSelectedItemId(item.id === calendarSelectedItemId ? null : item.id);
                        }}
                      >
                        <GripVertical className="h-4 w-4 text-muted-foreground opacity-50" />
                        {/* Image thumbnail or icon */}
                        {item.image_urls && item.image_urls.length > 0 ? (
                          <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 border border-border">
                            <img
                              src={item.image_urls[0]}
                              alt={item.name}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                // Hide on error and show icon
                                (e.target as HTMLImageElement).style.display = 'none';
                              }}
                            />
                          </div>
                        ) : (
                          <div
                            className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                            style={{ backgroundColor: `${config.color}40` }}
                          >
                            <Icon
                              className="h-5 w-5"
                              style={{ color: config.color }}
                            />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className={cn(
                              "font-medium text-sm",
                              isSelected && "text-[#284899]"
                            )}>{item.name}</p>
                            {item.operator_id && (
                              <Badge className="bg-emerald-500/15 text-emerald-600 text-[10px] px-1.5 py-0">
                                Database
                              </Badge>
                            )}
                            {item.category && (
                              <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                                {CATEGORIES.find(c => c.value === item.category)?.label || item.category}
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground flex-wrap">
                            <span>{config.label}</span>
                            {item.scheduled_date && (
                              <>
                                <span>•</span>
                                <span>
                                  {format(
                                    new Date(item.scheduled_date),
                                    "dd/MM/yyyy"
                                  )}
                                  {item.scheduled_time && ` ${item.scheduled_time}`}
                                </span>
                              </>
                            )}
                            {item.duration_minutes && (
                              <>
                                <span>•</span>
                                <span>{item.duration_minutes} min</span>
                              </>
                            )}
                            {/* Show location from item or operator metadata */}
                            {(item.city || (item.operator_id && (item.metadata as any)?.operator_city)) && (
                              <>
                                <span>•</span>
                                <span className="flex items-center gap-0.5">
                                  <MapPin className="h-2.5 w-2.5" />
                                  {item.city || (item.metadata as any)?.operator_city}
                                </span>
                              </>
                            )}
                            {/* Show languages if available */}
                            {item.languages && item.languages.length > 0 && (
                              <>
                                <span>•</span>
                                <span className="flex items-center gap-0.5">
                                  <Globe className="h-2.5 w-2.5" />
                                  {item.languages.length}
                                </span>
                              </>
                            )}
                            {/* Show image count if available */}
                            {item.image_urls && item.image_urls.length > 1 && (
                              <>
                                <span>•</span>
                                <span className="flex items-center gap-0.5">
                                  <Image className="h-2.5 w-2.5" />
                                  {item.image_urls.length}
                                </span>
                              </>
                            )}
                          </div>
                          {/* Tags row */}
                          {item.tags && item.tags.length > 0 && (
                            <div className="flex items-center gap-1 mt-1 flex-wrap">
                              {item.tags.slice(0, 3).map((tag, i) => (
                                <Badge
                                  key={`${tag}-${i}`}
                                  variant="outline"
                                  className="text-[9px] px-1 py-0 h-4"
                                >
                                  {tag}
                                </Badge>
                              ))}
                              {item.tags.length > 3 && (
                                <span className="text-[9px] text-muted-foreground">
                                  +{item.tags.length - 3}
                                </span>
                              )}
                            </div>
                          )}
                          {/* Included / Not included badges */}
                          {((item.included && item.included.length > 0) || (item.not_included && item.not_included.length > 0)) && (
                            <div className="flex items-center gap-1 mt-1 flex-wrap">
                              {item.included?.slice(0, 2).map((inc, i) => (
                                <Badge key={`inc-${i}`} className="bg-emerald-500/15 text-emerald-600 text-[9px] px-1 py-0 h-4">
                                  <CheckCircle2 className="h-2.5 w-2.5 mr-0.5" />{inc}
                                </Badge>
                              ))}
                              {(item.included?.length || 0) > 2 && (
                                <span className="text-[9px] text-emerald-600">+{(item.included?.length || 0) - 2}</span>
                              )}
                              {item.not_included?.slice(0, 2).map((exc, i) => (
                                <Badge key={`exc-${i}`} className="bg-red-500/15 text-red-600 text-[9px] px-1 py-0 h-4">
                                  <XCircle className="h-2.5 w-2.5 mr-0.5" />{exc}
                                </Badge>
                              ))}
                              {(item.not_included?.length || 0) > 2 && (
                                <span className="text-[9px] text-red-600">+{(item.not_included?.length || 0) - 2}</span>
                              )}
                            </div>
                          )}
                          {/* Contacts & Price */}
                          {(item.email_contact || item.phone_contact || item.price) && (
                            <div className="flex items-center gap-2 mt-1 text-[10px] text-muted-foreground flex-wrap">
                              {item.price && (
                                <span className="flex items-center gap-0.5">
                                  <Euro className="h-2.5 w-2.5" />
                                  {item.price}
                                </span>
                              )}
                              {item.email_contact && (
                                <span className="flex items-center gap-0.5">
                                  <Mail className="h-2.5 w-2.5" />
                                  {item.email_contact}
                                </span>
                              )}
                              {item.phone_contact && (
                                <span className="flex items-center gap-0.5">
                                  <Phone className="h-2.5 w-2.5" />
                                  {item.phone_contact}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-blue-500 hover:text-blue-600 hover:bg-blue-500/10"
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingItem(item);
                              setShowEditItemModal(true);
                            }}
                          >
                            <Edit3 className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-500/10"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteItem(item.id);
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </Reorder.Item>
                    );
                  })}
                </Reorder.Group>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Package className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Nessun servizio pianificato</p>
                  <p className="text-sm">
                    Aggiungi servizi alla pianificazione
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right: Calendar & Collaborators (3/5 width) */}
        <div className="lg:col-span-3 space-y-6">
          {/* Structured Calendar */}
          <Card className="overflow-hidden">
            <div className="h-[750px]">
              <PackageCalendar
                items={items}
                selectedItemId={calendarSelectedItemId}
                onSelectItem={setCalendarSelectedItemId}
                initialDate={pkg.start_date}
              />
            </div>
          </Card>
        </div>
      </div>

      {/* Edit Package Modal */}
      <AnimatePresence>
        {showEditModal && (
          <EditPackageModal
            package={pkg}
            onSave={async (updates) => {
              await updatePackage.mutateAsync({ id: pkg.id, ...updates });
              setShowEditModal(false);
            }}
            onClose={() => setShowEditModal(false)}
            isSaving={updatePackage.isPending}
          />
        )}
      </AnimatePresence>

      {/* Invite Collaborator Modal */}
      <AnimatePresence>
        {showInviteModal && (
          <InviteCollaboratorModal
            packageId={pkg.id}
            packageName={pkg.name}
            existingCollaborators={pkg.collaborators || []}
            onInvite={async (userId) => {
              await inviteCollaborator.mutateAsync({
                packageId: pkg.id,
                userId,
              });
              setShowInviteModal(false);
            }}
            onClose={() => setShowInviteModal(false)}
            isInviting={inviteCollaborator.isPending}
          />
        )}
      </AnimatePresence>

      {/* Add Item Modal */}
      <AddPackageItemModal
        isOpen={isAddingItem}
        packageId={pkg.id}
        itemsCount={items.length}
        onClose={() => setIsAddingItem(false)}
        onSuccess={() => {}}
      />

      {/* Edit Item Modal */}
      <EditPackageItemModal
        isOpen={showEditItemModal && !!editingItem}
        item={editingItem}
        onSave={async (updates) => {
          if (!editingItem) return;
          await updatePackageItem.mutateAsync({
            id: editingItem.id,
            packageId: pkg.id,
            ...updates,
          });
          setShowEditItemModal(false);
          setEditingItem(null);
        }}
        onClose={() => {
          setShowEditItemModal(false);
          setEditingItem(null);
        }}
        isSaving={updatePackageItem.isPending}
      />

      {/* Confirm Delete Item Dialog */}
      <ConfirmDialog
        isOpen={itemConfirmDialog.isOpen}
        title="Elimina Servizio"
        message="Sei sicuro di voler eliminare questo servizio dalla pianificazione?"
        confirmText="Elimina"
        variant="danger"
        onConfirm={confirmDeleteItem}
        onCancel={() => setItemConfirmDialog({ isOpen: false, itemId: "" })}
      />
    </div>
  );
}

function PdfPreviewModal({
  package: pkg,
  items,
  onClose,
}: {
  package: PackageType;
  items: PackageItem[];
  onClose: () => void;
}) {
  const handlePrint = () => {
    const printWindow = window.open("", "_blank");
    if (!printWindow) return;

    const itemsHtml = items
      .map((item) => {
        const config = itemTypeConfig[item.item_type];
        const emoji = item.item_type === "event" ? "📅" : item.item_type === "hotel" ? "🏨" : item.item_type === "activity" ? "🎯" : item.item_type === "attraction" ? "🏛️" : item.item_type === "transport" ? "🚐" : "📦";

        const metaParts: string[] = [config.label];
        if (item.scheduled_date) metaParts.push(format(new Date(item.scheduled_date), "dd MMMM yyyy", { locale: it }));
        if (item.scheduled_time) metaParts.push(item.scheduled_time);
        if (item.duration_minutes) metaParts.push(`${item.duration_minutes} min`);
        if (item.city) metaParts.push(`📍 ${item.city}`);
        if (item.category) {
          const cat = CATEGORIES.find(c => c.value === item.category);
          metaParts.push(cat?.label || item.category);
        }
        if (item.price) metaParts.push(`€ ${item.price}`);

        const includedHtml = item.included?.length
          ? `<div style="margin-top: 6px; display: flex; flex-wrap: wrap; gap: 4px;">${item.included.map(i => `<span style="display: inline-block; padding: 1px 6px; font-size: 11px; border-radius: 4px; background: #d1fae5; color: #059669;">✓ ${i}</span>`).join("")}</div>`
          : "";
        const notIncludedHtml = item.not_included?.length
          ? `<div style="margin-top: 4px; display: flex; flex-wrap: wrap; gap: 4px;">${item.not_included.map(i => `<span style="display: inline-block; padding: 1px 6px; font-size: 11px; border-radius: 4px; background: #fee2e2; color: #dc2626;">✗ ${i}</span>`).join("")}</div>`
          : "";
        const tagsHtml = item.tags?.length
          ? `<div style="margin-top: 4px; display: flex; flex-wrap: wrap; gap: 4px;">${item.tags.map(t => `<span style="display: inline-block; padding: 1px 6px; font-size: 11px; border-radius: 4px; background: #f1f5f9; color: #475569;">${t}</span>`).join("")}</div>`
          : "";
        const langHtml = item.languages?.length
          ? `<p style="margin: 4px 0 0; font-size: 11px; color: #6b7280;">🌐 ${item.languages.join(", ")}</p>`
          : "";
        const contactParts: string[] = [];
        if (item.email_contact) contactParts.push(`✉ ${item.email_contact}`);
        if (item.phone_contact) contactParts.push(`☎ ${item.phone_contact}`);
        const contactHtml = contactParts.length
          ? `<p style="margin: 4px 0 0; font-size: 11px; color: #6b7280;">${contactParts.join("  •  ")}</p>`
          : "";

        return `
        <div style="display: flex; align-items: flex-start; gap: 12px; padding: 12px; border: 1px solid #e5e7eb; border-radius: 8px; margin-bottom: 8px;">
          <div style="width: 40px; height: 40px; background: ${config.color}20; border-radius: 8px; display: flex; align-items: center; justify-content: center; flex-shrink: 0;">
            <span style="font-size: 18px;">${emoji}</span>
          </div>
          <div style="flex: 1; min-width: 0;">
            <h4 style="margin: 0; font-size: 14px; font-weight: 600;">${item.name}</h4>
            <p style="margin: 4px 0 0; font-size: 12px; color: #6b7280;">${metaParts.join(" • ")}</p>
            ${item.description ? `<p style="margin: 8px 0 0; font-size: 12px; color: #374151;">${item.description}</p>` : ""}
            ${includedHtml}${notIncludedHtml}${tagsHtml}${langHtml}${contactHtml}
          </div>
        </div>
      `;
      })
      .join("");

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>${pkg.name} - DPM Pianificazione Territoriale</title>
          <style>
            * { box-sizing: border-box; margin: 0; padding: 0; }
            body { font-family: system-ui, -apple-system, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
            h1 { font-size: 24px; color: #284899; margin-bottom: 8px; }
            h2 { font-size: 18px; color: #374151; margin: 24px 0 12px; }
            .header { border-bottom: 2px solid #284899; padding-bottom: 20px; margin-bottom: 24px; }
            .meta { display: flex; gap: 24px; color: #6b7280; font-size: 14px; margin-top: 12px; }
            .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #e5e7eb; text-align: center; color: #9ca3af; font-size: 12px; }
            @media print {
              body { padding: 20px; }
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>${pkg.name}</h1>
            ${pkg.description
        ? `<p style="color: #6b7280; margin-top: 8px;">${pkg.description}</p>`
        : ""
      }
            <div class="meta">
              ${pkg.start_date || pkg.end_date
        ? `<span>📅 ${pkg.start_date
          ? format(new Date(pkg.start_date), "dd MMMM yyyy", {
            locale: it,
          })
          : ""
        } ${pkg.start_date && pkg.end_date ? "-" : ""} ${pkg.end_date
          ? format(new Date(pkg.end_date), "dd MMMM yyyy", {
            locale: it,
          })
          : ""
        }</span>`
        : ""
      }
              <span>📦 ${items.length} servizi</span>
            </div>
          </div>
          
          <h2>Programma</h2>
          ${itemsHtml ||
      '<p style="color: #9ca3af; padding: 20px; text-align: center;">Nessun servizio pianificato</p>'
      }
          
          <div class="footer">
            <p>Generato da DPM - Destination Plan Machine</p>
            <p>${format(new Date(), "dd MMMM yyyy, HH:mm", { locale: it })}</p>
          </div>
          
          <script>window.print();</script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4 border-b border-border flex items-center justify-between">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <FileText className="h-5 w-5 text-[#284899]" />
            Anteprima PDF
          </h2>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-5 w-5" />
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 bg-white">
          {/* PDF Preview Content */}
          <div className="border-b-2 border-[#284899] pb-4 mb-6">
            <h1 className="text-2xl font-bold text-[#284899]">{pkg.name}</h1>
            {pkg.description && (
              <p className="text-gray-600 mt-2">{pkg.description}</p>
            )}
            <div className="flex gap-6 text-gray-500 text-sm mt-3">
              {(pkg.start_date || pkg.end_date) && (
                <span className="flex items-center gap-1">
                  <Calendar className="h-4 w-4" />
                  {pkg.start_date &&
                    format(new Date(pkg.start_date), "dd MMMM yyyy", {
                      locale: it,
                    })}
                  {pkg.start_date && pkg.end_date && " - "}
                  {pkg.end_date &&
                    format(new Date(pkg.end_date), "dd MMMM yyyy", {
                      locale: it,
                    })}
                </span>
              )}
              <span className="flex items-center gap-1">
                <Package className="h-4 w-4" />
                {items.length} servizi
              </span>
            </div>
          </div>

          <h2 className="text-lg font-semibold text-gray-800 mb-4">
            Programma
          </h2>

          {items.length > 0 ? (
            <div className="space-y-3">
              {items.map((item) => {
                const config = itemTypeConfig[item.item_type];
                const Icon = config.icon;
                return (
                  <div
                    key={item.id}
                    className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg"
                  >
                    <div
                      className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: `${config.color}40` }}
                    >
                      <Icon
                        className="h-5 w-5"
                        style={{ color: config.color }}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-sm text-gray-800">
                        {item.name}
                      </h4>
                      <p className="text-sm text-gray-500 mt-0.5">
                        {config.label}
                        {item.scheduled_date &&
                          ` • ${format(
                            new Date(item.scheduled_date),
                            "dd MMMM yyyy",
                            { locale: it }
                          )}`}
                        {item.scheduled_time && ` ${item.scheduled_time}`}
                        {item.duration_minutes && ` • ${item.duration_minutes} min`}
                        {item.city && ` • 📍 ${item.city}`}
                        {item.category && ` • ${CATEGORIES.find(c => c.value === item.category)?.label || item.category}`}
                        {item.price && ` • € ${item.price}`}
                      </p>
                      {item.description && (
                        <p className="text-sm text-gray-600 mt-2">
                          {item.description}
                        </p>
                      )}
                      {/* Included */}
                      {item.included && item.included.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1.5">
                          {item.included.map((inc, i) => (
                            <span key={`inc-${i}`} className="inline-block px-1.5 py-0.5 text-[11px] rounded bg-emerald-100 text-emerald-700">✓ {inc}</span>
                          ))}
                        </div>
                      )}
                      {/* Not included */}
                      {item.not_included && item.not_included.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {item.not_included.map((exc, i) => (
                            <span key={`exc-${i}`} className="inline-block px-1.5 py-0.5 text-[11px] rounded bg-red-100 text-red-700">✗ {exc}</span>
                          ))}
                        </div>
                      )}
                      {/* Tags */}
                      {item.tags && item.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {item.tags.map((tag, i) => (
                            <span key={`tag-${i}`} className="inline-block px-1.5 py-0.5 text-[11px] rounded bg-gray-100 text-gray-600">{tag}</span>
                          ))}
                        </div>
                      )}
                      {/* Languages */}
                      {item.languages && item.languages.length > 0 && (
                        <p className="text-[11px] text-gray-500 mt-1">🌐 {item.languages.join(", ")}</p>
                      )}
                      {/* Contacts */}
                      {(item.email_contact || item.phone_contact) && (
                        <p className="text-[11px] text-gray-500 mt-1">
                          {item.email_contact && `✉ ${item.email_contact}`}
                          {item.email_contact && item.phone_contact && "  •  "}
                          {item.phone_contact && `☎ ${item.phone_contact}`}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-center text-gray-400 py-8">
              Nessun servizio pianificato
            </p>
          )}

          <div className="mt-8 pt-4 border-t border-gray-200 text-center text-gray-400 text-sm">
            <p>Generato da DPM - Destination Plan Machine</p>
            <p>{format(new Date(), "dd MMMM yyyy, HH:mm", { locale: it })}</p>
          </div>
        </div>

        <div className="p-4 border-t border-border flex gap-3">
          <Button variant="outline" className="flex-1" onClick={onClose}>
            Chiudi
          </Button>
          <Button className="flex-1" onClick={handlePrint}>
            <Printer className="h-4 w-4 mr-2" />
            Stampa / Scarica PDF
          </Button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ============================================
// EDIT PACKAGE MODAL
// ============================================

function EditPackageModal({
  package: pkg,
  onSave,
  onClose,
  isSaving,
}: {
  package: PackageType;
  onSave: (updates: {
    name: string;
    description?: string;
    start_date?: string;
    end_date?: string;
  }) => Promise<void>;
  onClose: () => void;
  isSaving: boolean;
}) {
  const [formData, setFormData] = useState({
    name: pkg.name,
    description: pkg.description || "",
    start_date: pkg.start_date || "",
    end_date: pkg.end_date || "",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) return;

    await onSave({
      name: formData.name.trim(),
      description: formData.description.trim() || undefined,
      start_date: formData.start_date || undefined,
      end_date: formData.end_date || undefined,
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b border-border">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Edit3 className="h-5 w-5 text-[#284899]" />
            Modifica Pianificazione
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Nome Pianificazione *</label>
            <Input
              value={formData.name}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, name: e.target.value }))
              }
              placeholder="Es: Weekend in Toscana"
              required
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Descrizione</label>
            <textarea
              value={formData.description}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
              placeholder="Breve descrizione della pianificazione..."
              className="w-full h-24 rounded-lg border border-border bg-background px-3 py-2 text-sm resize-none focus:border-[#284899] focus:outline-none"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Data Inizio</label>
              <Input
                type="date"
                value={formData.start_date}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    start_date: e.target.value,
                  }))
                }
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Data Fine</label>
              <Input
                type="date"
                value={formData.end_date}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, end_date: e.target.value }))
                }
              />
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              onClick={onClose}
            >
              Annulla
            </Button>
            <Button
              type="submit"
              className="flex-1"
              disabled={isSaving || !formData.name.trim()}
            >
              {isSaving ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Check className="h-4 w-4 mr-2" />
              )}
              Salva Modifiche
            </Button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}

// ============================================
// INVITE COLLABORATOR MODAL
// ============================================

function EditPackageItemModal({
  isOpen,
  item,
  onSave,
  onClose,
  isSaving,
}: {
  isOpen: boolean;
  item: PackageItem | null;
  onSave: (updates: Partial<PackageItem>) => Promise<void>;
  onClose: () => void;
  isSaving: boolean;
}) {
  const [visibleFields, setVisibleFields] = useState<Set<ExtendedFieldKey>>(new Set());
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    scheduled_date: "",
    scheduled_time: "",
    duration_minutes: "" as string | number,
    location: "",
    address: "",
    city: "",
    province: "",
    latitude: null as number | null,
    longitude: null as number | null,
    image_urls: [] as string[],
    included: [] as string[],
    not_included: [] as string[],
    tags: [] as string[],
    category: "",
    languages: [] as string[],
    email_contact: "",
    phone_contact: "",
  });

  // Reset form data when item changes
  useEffect(() => {
    if (item) {
      setFormData({
        name: item.name,
        description: item.description || "",
        scheduled_date: item.scheduled_date || "",
        scheduled_time: item.scheduled_time || "",
        duration_minutes: item.duration_minutes || "",
        location: item.location || "",
        address: item.address || "",
        city: item.city || "",
        province: item.province || "",
        latitude: item.latitude,
        longitude: item.longitude,
        image_urls: item.image_urls || [],
        included: item.included || [],
        not_included: item.not_included || [],
        tags: item.tags || [],
        category: item.category || "",
        languages: item.languages || [],
        email_contact: item.email_contact || "",
        phone_contact: item.phone_contact || "",
      });
      setVisibleFields(getInitialVisibleFields(item));
    }
  }, [item]);

  const handleSubmit = async () => {
    await onSave({
      name: formData.name,
      description: formData.description || null,
      scheduled_date: formData.scheduled_date || null,
      scheduled_time: formData.scheduled_time || null,
      duration_minutes: formData.duration_minutes
        ? parseInt(formData.duration_minutes.toString())
        : null,
      location: formData.location || null,
      address: formData.address || null,
      city: formData.city || null,
      province: formData.province || null,
      latitude: formData.latitude || null,
      longitude: formData.longitude || null,
      image_urls: formData.image_urls.length > 0 ? formData.image_urls : null,
      included: formData.included.length > 0 ? formData.included : null,
      not_included: formData.not_included.length > 0 ? formData.not_included : null,
      tags: formData.tags.length > 0 ? formData.tags : null,
      category: formData.category || null,
      languages: formData.languages.length > 0 ? formData.languages : null,
      email_contact: formData.email_contact || null,
      phone_contact: formData.phone_contact || null,
    });
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title="Modifica Servizio"
      fullscreenOnMobile
    >
      {/* Content */}
      <div className="p-4 space-y-4">
          <div>
            <label className="text-sm font-medium text-muted-foreground mb-1.5 block">
              Nome Servizio
            </label>
            <Input
              value={formData.name}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, name: e.target.value }))
              }
              placeholder="Nome servizio"
            />
          </div>

          <div>
            <label className="text-sm font-medium text-muted-foreground mb-1.5 block">
              Descrizione
            </label>
            <textarea
              value={formData.description}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, description: e.target.value }))
              }
              placeholder="Descrizione del servizio..."
              className="w-full h-20 rounded-lg border border-border bg-background px-3 py-2 text-sm resize-none focus:border-[#284899] focus:outline-none"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-muted-foreground mb-1.5 block">
                Data
              </label>
              <Input
                type="date"
                value={formData.scheduled_date}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    scheduled_date: e.target.value,
                  }))
                }
              />
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground mb-1.5 block">
                Ora Inizio
              </label>
              <Input
                type="time"
                value={formData.scheduled_time}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    scheduled_time: e.target.value,
                  }))
                }
              />
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-muted-foreground mb-1.5 block">
              Durata (minuti)
            </label>
            <Input
              type="number"
              min="15"
              step="15"
              placeholder="60"
              value={formData.duration_minutes}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  duration_minutes: e.target.value,
                }))
              }
            />
          </div>

          {/* Extended Fields - Dropdown */}
          <div className="pt-4 border-t border-border space-y-4">
            <AddDetailDropdown
              visibleFields={visibleFields}
              onAdd={(field) => setVisibleFields((prev) => new Set(prev).add(field))}
            />

            <AnimatePresence>
              {visibleFields.has("location") && (
                <motion.div
                  key="location"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="space-y-3 overflow-hidden"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                      <MapPin className="h-4 w-4" />
                      Località
                    </div>
                    <button type="button" onClick={() => { setVisibleFields((prev) => { const s = new Set(prev); s.delete("location"); return s; }); setFormData((prev) => ({ ...prev, location: "", address: "", city: "", province: "", latitude: null, longitude: null })); }} className="text-muted-foreground hover:text-foreground"><X className="h-3.5 w-3.5" /></button>
                  </div>
                  <AddressAutocomplete
                    value={formData.location}
                    onValueChange={(value, address) => {
                      if (address) {
                        setFormData((prev) => ({
                          ...prev,
                          location: address.displayName,
                          address: address.address,
                          city: address.city,
                          province: address.province,
                          latitude: address.latitude,
                          longitude: address.longitude,
                        }));
                      } else {
                        setFormData((prev) => ({
                          ...prev,
                          location: value,
                        }));
                      }
                    }}
                    placeholder="Cerca indirizzo..."
                  />
                  {formData.city && (
                    <div className="text-sm text-muted-foreground flex items-center gap-2">
                      <Check className="h-3 w-3 text-emerald-500" />
                      {formData.city}
                      {formData.province && `, ${formData.province}`}
                    </div>
                  )}
                </motion.div>
              )}

              {visibleFields.has("category") && (
                <motion.div
                  key="category"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="space-y-3 overflow-hidden"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                      <Tag className="h-4 w-4" />
                      Categoria
                    </div>
                    <button type="button" onClick={() => { setVisibleFields((prev) => { const s = new Set(prev); s.delete("category"); return s; }); setFormData((prev) => ({ ...prev, category: "" })); }} className="text-muted-foreground hover:text-foreground"><X className="h-3.5 w-3.5" /></button>
                  </div>
                  <CategorySelect
                    value={formData.category || null}
                    onChange={(value) =>
                      setFormData((prev) => ({
                        ...prev,
                        category: value || "",
                      }))
                    }
                  />
                </motion.div>
              )}

              {visibleFields.has("images") && (
                <motion.div
                  key="images"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="space-y-3 overflow-hidden"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                      <Image className="h-4 w-4" />
                      Immagini
                    </div>
                    <button type="button" onClick={() => { setVisibleFields((prev) => { const s = new Set(prev); s.delete("images"); return s; }); setFormData((prev) => ({ ...prev, image_urls: [] })); }} className="text-muted-foreground hover:text-foreground"><X className="h-3.5 w-3.5" /></button>
                  </div>
                  <ImageGalleryInput
                    value={formData.image_urls}
                    onChange={(urls) =>
                      setFormData((prev) => ({
                        ...prev,
                        image_urls: urls,
                      }))
                    }
                    maxImages={5}
                  />
                </motion.div>
              )}

              {visibleFields.has("included") && (
                <motion.div
                  key="included"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="space-y-3 overflow-hidden"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                      <CheckCircle2 className="h-4 w-4" />
                      Incluso / Non incluso
                    </div>
                    <button type="button" onClick={() => { setVisibleFields((prev) => { const s = new Set(prev); s.delete("included"); return s; }); setFormData((prev) => ({ ...prev, included: [], not_included: [] })); }} className="text-muted-foreground hover:text-foreground"><X className="h-3.5 w-3.5" /></button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
                        <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                        Cosa è incluso
                      </div>
                      <ArrayFieldInput
                        value={formData.included}
                        onChange={(items) =>
                          setFormData((prev) => ({
                            ...prev,
                            included: items,
                          }))
                        }
                        placeholder="Es: Pranzo, Trasporto..."
                        badgeVariant="success"
                      />
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
                        <XCircle className="h-3.5 w-3.5 text-red-500" />
                        Cosa NON è incluso
                      </div>
                      <ArrayFieldInput
                        value={formData.not_included}
                        onChange={(items) =>
                          setFormData((prev) => ({
                            ...prev,
                            not_included: items,
                          }))
                        }
                        placeholder="Es: Bevande, Assicurazione..."
                        badgeVariant="error"
                      />
                    </div>
                  </div>
                </motion.div>
              )}

              {visibleFields.has("tags") && (
                <motion.div
                  key="tags"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="space-y-3 overflow-hidden"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                      <Tag className="h-4 w-4" />
                      Etichette
                    </div>
                    <button type="button" onClick={() => { setVisibleFields((prev) => { const s = new Set(prev); s.delete("tags"); return s; }); setFormData((prev) => ({ ...prev, tags: [] })); }} className="text-muted-foreground hover:text-foreground"><X className="h-3.5 w-3.5" /></button>
                  </div>
                  <ArrayFieldInput
                    value={formData.tags}
                    onChange={(items) =>
                      setFormData((prev) => ({
                        ...prev,
                        tags: items,
                      }))
                    }
                    placeholder="Es: romantico, avventura..."
                    badgeVariant="default"
                  />
                </motion.div>
              )}

              {visibleFields.has("languages") && (
                <motion.div
                  key="languages"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="space-y-3 overflow-hidden"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                      <Globe className="h-4 w-4" />
                      Lingue supportate
                    </div>
                    <button type="button" onClick={() => { setVisibleFields((prev) => { const s = new Set(prev); s.delete("languages"); return s; }); setFormData((prev) => ({ ...prev, languages: [] })); }} className="text-muted-foreground hover:text-foreground"><X className="h-3.5 w-3.5" /></button>
                  </div>
                  <LanguageSelect
                    value={formData.languages}
                    onChange={(langs) =>
                      setFormData((prev) => ({
                        ...prev,
                        languages: langs,
                      }))
                    }
                  />
                </motion.div>
              )}

              {visibleFields.has("contacts") && (
                <motion.div
                  key="contacts"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="space-y-3 overflow-hidden"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                      <Mail className="h-4 w-4" />
                      Contatti
                    </div>
                    <button type="button" onClick={() => { setVisibleFields((prev) => { const s = new Set(prev); s.delete("contacts"); return s; }); setFormData((prev) => ({ ...prev, email_contact: "", phone_contact: "" })); }} className="text-muted-foreground hover:text-foreground"><X className="h-3.5 w-3.5" /></button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Input
                      icon={<Mail className="h-4 w-4" />}
                      type="email"
                      placeholder="Email contatto"
                      value={formData.email_contact}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          email_contact: e.target.value,
                        }))
                      }
                    />
                    <Input
                      icon={<Phone className="h-4 w-4" />}
                      type="tel"
                      placeholder="Telefono contatto"
                      value={formData.phone_contact}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          phone_contact: e.target.value,
                        }))
                      }
                    />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

      {/* Footer */}
      <div className="p-4 border-t border-border bg-muted/10 flex gap-2 shrink-0">
        <Button variant="outline" className="flex-1" onClick={onClose}>
          Annulla
        </Button>
        <Button
          className="flex-1"
          onClick={handleSubmit}
          disabled={isSaving || !formData.name.trim()}
        >
          {isSaving ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Salvataggio...
            </>
          ) : (
            <>
              <Check className="h-4 w-4 mr-2" />
              Salva
            </>
          )}
        </Button>
      </div>
    </Dialog>
  );
}

function AddPackageItemModal({
  isOpen,
  packageId,
  itemsCount,
  onClose,
  onSuccess,
}: {
  isOpen: boolean;
  packageId: string;
  itemsCount: number;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [mode, setMode] = useState<"select" | "create">("select");
  const [saveToMyServices, setSaveToMyServices] = useState(false);
  const [selectedService, setSelectedService] = useState<Service | null>(null);

  // Override fields for selected service
  const [overrideDate, setOverrideDate] = useState("");
  const [overrideTime, setOverrideTime] = useState("");
  const [overrideDuration, setOverrideDuration] = useState<number | "">("");

  // "Create" mode state (same as before)
  const [visibleFields, setVisibleFields] = useState<Set<ExtendedFieldKey>>(new Set());
  const [newItem, setNewItem] = useState<Partial<PackageItem>>({
    item_type: "event",
    name: "",
    description: "",
    scheduled_date: "",
    location: null,
    address: null,
    city: null,
    province: null,
    latitude: null,
    longitude: null,
    image_urls: null,
    included: null,
    not_included: null,
    tags: null,
    category: null,
    languages: null,
    email_contact: null,
    phone_contact: null,
  });
  const [operatorSearch, setOperatorSearch] = useState("");
  const [selectedOperator, setSelectedOperator] = useState<Operator | null>(null);
  const [showOperatorResults, setShowOperatorResults] = useState(false);
  const [serviceSearch, setServiceSearch] = useState("");

  const addPackageItem = useAddPackageItem();
  const createService = useCreateService();
  const { data: myServices = [] } = useServices();
  const { data: searchResults = [], isLoading: searchLoading } =
    useSearchOperators(operatorSearch);

  const filteredServices = useMemo(() => {
    if (!serviceSearch.trim()) return myServices;
    const q = serviceSearch.toLowerCase();
    return myServices.filter(
      (s) =>
        s.name.toLowerCase().includes(q) ||
        s.city?.toLowerCase().includes(q) ||
        s.category?.toLowerCase().includes(q)
    );
  }, [myServices, serviceSearch]);

  const resetForm = () => {
    setNewItem({
      item_type: "event",
      name: "",
      description: "",
      scheduled_date: "",
      scheduled_time: "",
      duration_minutes: undefined,
      location: null,
      address: null,
      city: null,
      province: null,
      latitude: null,
      longitude: null,
      image_urls: null,
      included: null,
      not_included: null,
      tags: null,
      category: null,
      languages: null,
      email_contact: null,
      phone_contact: null,
    });
    setSelectedOperator(null);
    setOperatorSearch("");
    setVisibleFields(new Set());
    setShowOperatorResults(false);
    setSelectedService(null);
    setOverrideDate("");
    setOverrideTime("");
    setOverrideDuration("");
    setSaveToMyServices(false);
    setServiceSearch("");
  };

  const handleSelectOperator = (operator: Operator) => {
    setSelectedOperator(operator);
    const typologyLower = operator.typology?.toLowerCase() || "";
    let itemType: PackageItemType = "activity";
    if (typologyLower.includes("hotel") || typologyLower.includes("accommodation") || typologyLower.includes("resort")) {
      itemType = "hotel";
    } else if (typologyLower.includes("attraction") || typologyLower.includes("museum") || typologyLower.includes("monument")) {
      itemType = "attraction";
    } else if (typologyLower.includes("transport") || typologyLower.includes("transfer") || typologyLower.includes("shuttle")) {
      itemType = "transport";
    } else if (typologyLower.includes("event") || typologyLower.includes("festival") || typologyLower.includes("show")) {
      itemType = "event";
    }
    setNewItem((prev) => ({
      ...prev,
      name: operator.name,
      operator_id: operator.id,
      item_type: itemType,
    }));
    setOperatorSearch("");
    setShowOperatorResults(false);
  };

  const handleSubmitCreate = async () => {
    if (!newItem.name?.trim()) return;

    try {
      await addPackageItem.mutateAsync({
        package_id: packageId,
        item_type: newItem.item_type || "event",
        name: newItem.name,
        description: newItem.description || null,
        scheduled_date: newItem.scheduled_date || null,
        scheduled_time: newItem.scheduled_time || null,
        duration_minutes: newItem.duration_minutes || null,
        price: null,
        notes: null,
        sort_order: itemsCount,
        operator_id: selectedOperator?.id || null,
        location: newItem.location || null,
        address: newItem.address || null,
        city: newItem.city || null,
        province: newItem.province || null,
        latitude: newItem.latitude || null,
        longitude: newItem.longitude || null,
        image_urls: newItem.image_urls?.length ? newItem.image_urls : null,
        included: newItem.included?.length ? newItem.included : null,
        not_included: newItem.not_included?.length ? newItem.not_included : null,
        tags: newItem.tags?.length ? newItem.tags : null,
        category: newItem.category || null,
        languages: newItem.languages?.length ? newItem.languages : null,
        email_contact: newItem.email_contact || null,
        phone_contact: newItem.phone_contact || null,
        metadata: selectedOperator
          ? {
              operator_city: selectedOperator.city,
              operator_region: selectedOperator.region,
              operator_typology: selectedOperator.typology,
            }
          : {},
      });

      // Save to my services if checkbox is checked
      if (saveToMyServices) {
        try {
          await createService.mutateAsync({
            item_type: newItem.item_type || "event",
            name: newItem.name!,
            description: newItem.description || null,
            scheduled_date: newItem.scheduled_date || null,
            scheduled_time: newItem.scheduled_time || null,
            duration_minutes: newItem.duration_minutes || null,
            price: null,
            notes: null,
            metadata: selectedOperator
              ? {
                  operator_city: selectedOperator.city,
                  operator_region: selectedOperator.region,
                  operator_typology: selectedOperator.typology,
                }
              : {},
            operator_id: selectedOperator?.id || null,
            location: newItem.location || null,
            address: newItem.address || null,
            city: newItem.city || null,
            province: newItem.province || null,
            latitude: newItem.latitude || null,
            longitude: newItem.longitude || null,
            image_urls: newItem.image_urls?.length ? newItem.image_urls : null,
            included: newItem.included?.length ? newItem.included : null,
            not_included: newItem.not_included?.length ? newItem.not_included : null,
            tags: newItem.tags?.length ? newItem.tags : null,
            category: newItem.category || null,
            languages: newItem.languages?.length ? newItem.languages : null,
            email_contact: newItem.email_contact || null,
            phone_contact: newItem.phone_contact || null,
          });
        } catch (err) {
          console.error("Error saving to my services:", err);
        }
      }

      resetForm();
      onSuccess();
      onClose();
    } catch (error) {
      console.error("Error adding item:", error);
    }
  };

  const handleSubmitSelect = async () => {
    if (!selectedService) return;

    try {
      await addPackageItem.mutateAsync({
        package_id: packageId,
        item_type: selectedService.item_type,
        name: selectedService.name,
        description: selectedService.description || null,
        scheduled_date: overrideDate || selectedService.scheduled_date || null,
        scheduled_time: overrideTime || selectedService.scheduled_time || null,
        duration_minutes: overrideDuration ? Number(overrideDuration) : selectedService.duration_minutes || null,
        price: selectedService.price || null,
        notes: selectedService.notes || null,
        sort_order: itemsCount,
        operator_id: selectedService.operator_id || null,
        location: selectedService.location || null,
        address: selectedService.address || null,
        city: selectedService.city || null,
        province: selectedService.province || null,
        latitude: selectedService.latitude || null,
        longitude: selectedService.longitude || null,
        image_urls: selectedService.image_urls?.length ? selectedService.image_urls : null,
        included: selectedService.included?.length ? selectedService.included : null,
        not_included: selectedService.not_included?.length ? selectedService.not_included : null,
        tags: selectedService.tags?.length ? selectedService.tags : null,
        category: selectedService.category || null,
        languages: selectedService.languages?.length ? selectedService.languages : null,
        email_contact: selectedService.email_contact || null,
        phone_contact: selectedService.phone_contact || null,
        metadata: selectedService.metadata || {},
      });
      resetForm();
      onSuccess();
      onClose();
    } catch (error) {
      console.error("Error adding item from service:", error);
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Dialog isOpen={isOpen} onClose={handleClose} title="Aggiungi Servizio" fullscreenOnMobile>
      {/* Tab bar */}
      <div className="flex border-b border-border shrink-0">
        <button
          type="button"
          className={cn(
            "flex-1 px-4 py-3 text-sm font-medium transition-colors text-center",
            mode === "select"
              ? "text-[#284899] border-b-2 border-[#284899] bg-[#284899]/5"
              : "text-muted-foreground hover:text-foreground"
          )}
          onClick={() => setMode("select")}
        >
          Dai tuoi servizi
        </button>
        <button
          type="button"
          className={cn(
            "flex-1 px-4 py-3 text-sm font-medium transition-colors text-center",
            mode === "create"
              ? "text-[#284899] border-b-2 border-[#284899] bg-[#284899]/5"
              : "text-muted-foreground hover:text-foreground"
          )}
          onClick={() => setMode("create")}
        >
          Crea nuovo
        </button>
      </div>

      <div className="p-4 space-y-4">
        {mode === "select" ? (
          <>
            {/* Service search / filter */}
            <Input
              placeholder="Cerca tra i tuoi servizi..."
              value={serviceSearch}
              onChange={(e) => setServiceSearch(e.target.value)}
              icon={<Search className="h-4 w-4" />}
            />

            {myServices.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Briefcase className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">Nessun servizio salvato</p>
                <p className="text-sm">Passa alla tab "Crea nuovo" per creare un servizio</p>
              </div>
            ) : filteredServices.length === 0 ? (
              <div className="text-center py-6 text-muted-foreground">
                <p className="text-sm">Nessun servizio trovato</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {filteredServices.map((service) => {
                  const config = itemTypeConfig[service.item_type];
                  const Icon = config.icon;
                  const isSelected = selectedService?.id === service.id;
                  return (
                    <button
                      key={service.id}
                      type="button"
                      className={cn(
                        "w-full flex items-center gap-3 p-3 rounded-lg border text-left transition-all",
                        isSelected
                          ? "bg-[#284899]/10 border-[#284899] ring-1 ring-[#284899]/30"
                          : "bg-secondary/30 border-border hover:border-[#284899]/30"
                      )}
                      onClick={() => {
                        setSelectedService(isSelected ? null : service);
                        if (!isSelected) {
                          setOverrideDate(service.scheduled_date || "");
                          setOverrideTime(service.scheduled_time || "");
                          setOverrideDuration(service.duration_minutes || "");
                        }
                      }}
                    >
                      <div
                        className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: `${config.color}40` }}
                      >
                        <Icon className="h-4 w-4" style={{ color: config.color }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className={cn("font-medium text-sm truncate", isSelected && "text-[#284899]")}>{service.name}</p>
                          <Badge
                            className="text-[10px] px-1.5 py-0 shrink-0"
                            style={{ backgroundColor: `${config.color}20`, color: config.color }}
                          >
                            {config.label}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                          {service.city && (
                            <span className="flex items-center gap-0.5">
                              <MapPin className="h-2.5 w-2.5" />
                              {service.city}
                            </span>
                          )}
                          {service.category && (
                            <span>{CATEGORIES.find(c => c.value === service.category)?.label || service.category}</span>
                          )}
                          {service.price && (
                            <span>€ {service.price}</span>
                          )}
                        </div>
                      </div>
                      {isSelected && (
                        <Check className="h-5 w-5 text-[#284899] shrink-0" />
                      )}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Override scheduling for selected service */}
            {selectedService && (
              <div className="pt-4 border-t border-border space-y-3">
                <p className="text-sm font-medium text-muted-foreground">Personalizza data/ora per la pianificazione:</p>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="text-xs font-medium text-muted-foreground mb-1 block">Data</label>
                    <Input
                      type="date"
                      value={overrideDate}
                      onChange={(e) => setOverrideDate(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground mb-1 block">Ora inizio</label>
                    <Input
                      type="time"
                      value={overrideTime}
                      onChange={(e) => setOverrideTime(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground mb-1 block">Durata (min)</label>
                    <Input
                      type="number"
                      min="15"
                      step="15"
                      placeholder="60"
                      value={overrideDuration}
                      onChange={(e) => setOverrideDuration(e.target.value ? parseInt(e.target.value) : "")}
                    />
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            {/* CREATE mode — same as original */}
            {/* Operator Search */}
            <div>
              <label className="text-sm font-medium text-muted-foreground mb-1.5 flex items-center gap-1.5">
                <Search className="h-3.5 w-3.5" />
                Cerca operatore dal database
              </label>
              <div className="relative">
                <Input
                  placeholder="Cerca per nome o città..."
                  value={operatorSearch}
                  onChange={(e) => {
                    setOperatorSearch(e.target.value);
                    setShowOperatorResults(e.target.value.length >= 2);
                  }}
                  onFocus={() => operatorSearch.length >= 2 && setShowOperatorResults(true)}
                />
                {showOperatorResults && (
                  <motion.div
                    initial={{ opacity: 0, y: -4 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="absolute z-50 mt-1 w-full max-h-64 overflow-auto rounded-lg border border-border bg-card shadow-lg"
                  >
                    {searchLoading ? (
                      <div className="p-3 text-center text-sm text-muted-foreground">Ricerca...</div>
                    ) : searchResults.length === 0 ? (
                      <div className="p-3 text-center text-sm text-muted-foreground">Nessun risultato</div>
                    ) : (
                      searchResults.map((op) => (
                        <button
                          key={op.id}
                          type="button"
                          className="w-full px-3 py-2 text-left hover:bg-secondary/50 transition-colors"
                          onClick={() => handleSelectOperator(op)}
                        >
                          <div className="font-medium text-sm truncate">{op.name}</div>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <MapPin className="h-3 w-3" />
                            {op.city} - <span className="text-[#284899]">{op.typology}</span>
                          </div>
                        </button>
                      ))
                    )}
                  </motion.div>
                )}
              </div>

              {selectedOperator && (
                <div className="mt-2 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-emerald-500" />
                      <span className="text-sm font-medium">{selectedOperator.name}</span>
                      <Badge className="bg-emerald-500/20 text-emerald-600 text-[10px]">Da Database</Badge>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => {
                        setSelectedOperator(null);
                        setNewItem((prev) => ({ ...prev, name: "", operator_id: undefined }));
                      }}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-muted-foreground">
                    {selectedOperator.city && (
                      <span className="flex items-center gap-1">
                        <MapPin className="h-3 w-3" />
                        {selectedOperator.city}
                      </span>
                    )}
                    {selectedOperator.typology && <span className="text-[#284899]">{selectedOperator.typology}</span>}
                  </div>
                </div>
              )}
            </div>

            {/* Manual entry section */}
            {!selectedOperator && (
              <>
                <div className="flex items-center gap-2">
                  <div className="h-px flex-1 bg-border" />
                  <span className="text-sm text-muted-foreground">oppure inserisci manualmente</span>
                  <div className="h-px flex-1 bg-border" />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <select
                    value={newItem.item_type}
                    onChange={(e) => setNewItem((prev) => ({ ...prev, item_type: e.target.value as PackageItemType }))}
                    className="h-9 rounded-lg border border-border bg-card px-3 text-sm focus:border-[#284899] focus:outline-none"
                  >
                    {Object.entries(itemTypeConfig).map(([key, config]) => (
                      <option key={key} value={key}>{config.label}</option>
                    ))}
                  </select>
                  <Input
                    placeholder="Nome servizio"
                    value={newItem.name || ""}
                    onChange={(e) => setNewItem((prev) => ({ ...prev, name: e.target.value }))}
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="text-sm font-medium text-muted-foreground mb-1.5 block">Descrizione</label>
                  <textarea
                    placeholder="Descrizione del servizio..."
                    value={newItem.description || ""}
                    onChange={(e) => setNewItem((prev) => ({ ...prev, description: e.target.value }))}
                    className="w-full h-20 rounded-lg border border-border bg-background px-3 py-2 text-sm resize-none focus:border-[#284899] focus:outline-none"
                  />
                </div>
              </>
            )}

            {/* Date/Time fields */}
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-sm font-medium text-muted-foreground mb-1.5 block">Data</label>
                <Input
                  type="date"
                  value={newItem.scheduled_date || ""}
                  onChange={(e) => setNewItem((prev) => ({ ...prev, scheduled_date: e.target.value }))}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground mb-1.5 block">Ora inizio</label>
                <Input
                  type="time"
                  value={newItem.scheduled_time || ""}
                  onChange={(e) => setNewItem((prev) => ({ ...prev, scheduled_time: e.target.value }))}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground mb-1.5 block">Durata (min)</label>
                <Input
                  type="number"
                  min="15"
                  step="15"
                  placeholder="60"
                  value={newItem.duration_minutes || ""}
                  onChange={(e) => setNewItem((prev) => ({ ...prev, duration_minutes: e.target.value ? parseInt(e.target.value) : undefined }))}
                />
              </div>
            </div>

            {/* Extended Fields - Dropdown */}
            <div className="pt-4 border-t border-border space-y-4">
              <AddDetailDropdown
                visibleFields={visibleFields}
                onAdd={(field) => setVisibleFields((prev) => new Set(prev).add(field))}
              />

              <AnimatePresence>
                {visibleFields.has("location") && (
                  <motion.div key="location" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="space-y-3 overflow-hidden">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground"><MapPin className="h-4 w-4" />Località</div>
                      <button type="button" onClick={() => { setVisibleFields((prev) => { const s = new Set(prev); s.delete("location"); return s; }); setNewItem((prev) => ({ ...prev, location: null, address: null, city: null, province: null, latitude: null, longitude: null })); }} className="text-muted-foreground hover:text-foreground"><X className="h-3.5 w-3.5" /></button>
                    </div>
                    <AddressAutocomplete
                      value={newItem.location || ""}
                      onValueChange={(value, address) => {
                        if (address) {
                          setNewItem((prev) => ({ ...prev, location: address.displayName, address: address.address, city: address.city, province: address.province, latitude: address.latitude, longitude: address.longitude }));
                        } else {
                          setNewItem((prev) => ({ ...prev, location: value || null }));
                        }
                      }}
                      placeholder="Cerca indirizzo..."
                    />
                    {newItem.city && (
                      <div className="text-sm text-muted-foreground flex items-center gap-2">
                        <Check className="h-3 w-3 text-emerald-500" />
                        {newItem.city}{newItem.province && `, ${newItem.province}`}
                      </div>
                    )}
                  </motion.div>
                )}

                {visibleFields.has("category") && (
                  <motion.div key="category" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="space-y-3 overflow-hidden">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground"><Tag className="h-4 w-4" />Categoria</div>
                      <button type="button" onClick={() => { setVisibleFields((prev) => { const s = new Set(prev); s.delete("category"); return s; }); setNewItem((prev) => ({ ...prev, category: null })); }} className="text-muted-foreground hover:text-foreground"><X className="h-3.5 w-3.5" /></button>
                    </div>
                    <CategorySelect value={newItem.category || null} onChange={(value) => setNewItem((prev) => ({ ...prev, category: value }))} />
                  </motion.div>
                )}

                {visibleFields.has("images") && (
                  <motion.div key="images" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="space-y-3 overflow-hidden">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground"><Image className="h-4 w-4" />Immagini</div>
                      <button type="button" onClick={() => { setVisibleFields((prev) => { const s = new Set(prev); s.delete("images"); return s; }); setNewItem((prev) => ({ ...prev, image_urls: null })); }} className="text-muted-foreground hover:text-foreground"><X className="h-3.5 w-3.5" /></button>
                    </div>
                    <ImageGalleryInput value={newItem.image_urls || []} onChange={(urls) => setNewItem((prev) => ({ ...prev, image_urls: urls.length > 0 ? urls : null }))} maxImages={5} />
                  </motion.div>
                )}

                {visibleFields.has("included") && (
                  <motion.div key="included" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="space-y-3 overflow-hidden">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground"><CheckCircle2 className="h-4 w-4" />Incluso / Non incluso</div>
                      <button type="button" onClick={() => { setVisibleFields((prev) => { const s = new Set(prev); s.delete("included"); return s; }); setNewItem((prev) => ({ ...prev, included: null, not_included: null })); }} className="text-muted-foreground hover:text-foreground"><X className="h-3.5 w-3.5" /></button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />Cosa è incluso</div>
                        <ArrayFieldInput value={newItem.included || []} onChange={(items) => setNewItem((prev) => ({ ...prev, included: items.length > 0 ? items : null }))} placeholder="Es: Pranzo, Trasporto..." badgeVariant="success" />
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground"><XCircle className="h-3.5 w-3.5 text-red-500" />Cosa NON è incluso</div>
                        <ArrayFieldInput value={newItem.not_included || []} onChange={(items) => setNewItem((prev) => ({ ...prev, not_included: items.length > 0 ? items : null }))} placeholder="Es: Bevande, Assicurazione..." badgeVariant="error" />
                      </div>
                    </div>
                  </motion.div>
                )}

                {visibleFields.has("tags") && (
                  <motion.div key="tags" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="space-y-3 overflow-hidden">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground"><Tag className="h-4 w-4" />Etichette</div>
                      <button type="button" onClick={() => { setVisibleFields((prev) => { const s = new Set(prev); s.delete("tags"); return s; }); setNewItem((prev) => ({ ...prev, tags: null })); }} className="text-muted-foreground hover:text-foreground"><X className="h-3.5 w-3.5" /></button>
                    </div>
                    <ArrayFieldInput value={newItem.tags || []} onChange={(items) => setNewItem((prev) => ({ ...prev, tags: items.length > 0 ? items : null }))} placeholder="Es: romantico, avventura..." badgeVariant="default" />
                  </motion.div>
                )}

                {visibleFields.has("languages") && (
                  <motion.div key="languages" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="space-y-3 overflow-hidden">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground"><Globe className="h-4 w-4" />Lingue supportate</div>
                      <button type="button" onClick={() => { setVisibleFields((prev) => { const s = new Set(prev); s.delete("languages"); return s; }); setNewItem((prev) => ({ ...prev, languages: null })); }} className="text-muted-foreground hover:text-foreground"><X className="h-3.5 w-3.5" /></button>
                    </div>
                    <LanguageSelect value={newItem.languages || []} onChange={(langs) => setNewItem((prev) => ({ ...prev, languages: langs.length > 0 ? langs : null }))} />
                  </motion.div>
                )}

                {visibleFields.has("contacts") && (
                  <motion.div key="contacts" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="space-y-3 overflow-hidden">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground"><Mail className="h-4 w-4" />Contatti</div>
                      <button type="button" onClick={() => { setVisibleFields((prev) => { const s = new Set(prev); s.delete("contacts"); return s; }); setNewItem((prev) => ({ ...prev, email_contact: null, phone_contact: null })); }} className="text-muted-foreground hover:text-foreground"><X className="h-3.5 w-3.5" /></button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <Input icon={<Mail className="h-4 w-4" />} type="email" placeholder="Email contatto" value={newItem.email_contact || ""} onChange={(e) => setNewItem((prev) => ({ ...prev, email_contact: e.target.value || null }))} />
                      <Input icon={<Phone className="h-4 w-4" />} type="tel" placeholder="Telefono contatto" value={newItem.phone_contact || ""} onChange={(e) => setNewItem((prev) => ({ ...prev, phone_contact: e.target.value || null }))} />
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Save to my services checkbox */}
            <div className="pt-4 border-t border-border">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={saveToMyServices}
                  onChange={(e) => setSaveToMyServices(e.target.checked)}
                  className="h-4 w-4 rounded border-border text-[#284899] focus:ring-[#284899]"
                />
                <span className="text-sm text-muted-foreground">
                  Salva nei tuoi servizi
                </span>
              </label>
            </div>
          </>
        )}
      </div>

      {/* Footer */}
      <div className="p-4 border-t border-border bg-muted/10 flex gap-2 shrink-0">
        <Button variant="outline" className="flex-1" onClick={handleClose}>
          Annulla
        </Button>
        <Button
          className="flex-1"
          onClick={mode === "select" ? handleSubmitSelect : handleSubmitCreate}
          disabled={
            addPackageItem.isPending ||
            (mode === "select" ? !selectedService : !newItem.name?.trim())
          }
        >
          {addPackageItem.isPending ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Aggiunta...
            </>
          ) : (
            <>
              <Plus className="h-4 w-4 mr-2" />
              Aggiungi Servizio
            </>
          )}
        </Button>
      </div>
    </Dialog>
  );
}

function InviteCollaboratorModal({
  packageName,
  existingCollaborators,
  onInvite,
  onClose,
  isInviting,
}: {
  packageId: string;
  packageName: string;
  existingCollaborators: PackageType["collaborators"];
  onInvite: (userId: string) => Promise<void>;
  onClose: () => void;
  isInviting: boolean;
}) {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedUser, setSelectedUser] = useState<{
    id: string;
    name: string;
    email: string;
  } | null>(null);
  const [showResults, setShowResults] = useState(false);

  // Use user search to find users
  const { data: searchResults = [], isLoading: searchLoading } =
    useSearchUsers(searchQuery, 20);

  // Filter out existing collaborators
  const existingIds = new Set(
    (existingCollaborators || []).map((c) => c.user_id)
  );
  const filteredResults = searchResults.filter((user) => !existingIds.has(user.id));

  const handleSelectUser = (user: User) => {
    const userName = [user.first_name, user.last_name].filter(Boolean).join(" ") || "Utente";
    setSelectedUser({
      id: user.id,
      name: userName,
      email: user.email || "",
    });
    setSearchQuery("");
    setShowResults(false);
  };

  const handleInvite = async () => {
    if (!selectedUser) return;
    await onInvite(selectedUser.id);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b border-border">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-[#284899]" />
            Invita Collaboratore
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Invita un utente a collaborare su "{packageName}"
          </p>
        </div>

        <div className="p-6 space-y-4">
          {/* Search */}
          <div className="space-y-2">
            <label className="text-sm font-medium flex items-center gap-2">
              <Search className="h-4 w-4 text-muted-foreground" />
              Cerca utente
            </label>
            <div className="relative">
              <Input
                placeholder="Cerca per nome o email..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setShowResults(e.target.value.length >= 2);
                }}
                onFocus={() => searchQuery.length >= 2 && setShowResults(true)}
              />
              {showResults && (
                <motion.div
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="absolute z-50 mt-1 w-full max-h-64 overflow-auto rounded-lg border border-border bg-card shadow-lg"
                >
                  {searchLoading ? (
                    <div className="p-4 text-center text-sm text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin mx-auto mb-2" />
                      Ricerca in corso...
                    </div>
                  ) : filteredResults.length === 0 ? (
                    <div className="p-4 text-center text-sm text-muted-foreground">
                      Nessun utente trovato
                    </div>
                  ) : (
                    filteredResults.map((user) => {
                      const userName = [user.first_name, user.last_name].filter(Boolean).join(" ") || "Utente";
                      return (
                        <button
                          key={user.id}
                          type="button"
                          className="w-full px-4 py-3 text-left hover:bg-secondary/50 transition-colors border-b border-border last:border-b-0"
                          onClick={() => handleSelectUser(user)}
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-[#284899]/20 flex items-center justify-center">
                              <Users className="h-4 w-4 text-[#284899]" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm truncate">
                                {userName}
                              </p>
                              <p className="text-sm text-muted-foreground truncate">
                                {user.email}
                                {user.role && (
                                  <span className="text-[#284899] ml-1">
                                    • {getRoleLabel(user.role)}
                                  </span>
                                )}
                              </p>
                            </div>
                          </div>
                        </button>
                      );
                    })
                  )}
                </motion.div>
              )}
            </div>
          </div>

          {/* Selected User */}
          {selectedUser && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/30"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
                    <Check className="h-5 w-5 text-emerald-500" />
                  </div>
                  <div>
                    <p className="font-medium">{selectedUser.name}</p>
                    {selectedUser.email && (
                      <p className="text-sm text-muted-foreground">
                        {selectedUser.email}
                      </p>
                    )}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => setSelectedUser(null)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </motion.div>
          )}

          {/* Info */}
          <div className="p-3 rounded-lg bg-secondary/30 border border-border">
            <p className="text-sm text-muted-foreground">
              <strong className="text-foreground">Nota:</strong> L'utente
              invitato riceverà una notifica e dovrà accettare l'invito. Il
              pianificazione non potrà essere pubblicata finché tutti i collaboratori
              non avranno accettato.
            </p>
          </div>
        </div>

        <div className="p-6 border-t border-border flex gap-3">
          <Button variant="outline" className="flex-1" onClick={onClose}>
            Annulla
          </Button>
          <Button
            className="flex-1"
            onClick={handleInvite}
            disabled={!selectedUser || isInviting}
          >
            {isInviting ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Send className="h-4 w-4 mr-2" />
            )}
            Invia Invito
          </Button>
        </div>
      </motion.div>
    </motion.div>
  );
}
